import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Sparkles, Copy, Bookmark, Loader2, Lightbulb, FileText, PenTool } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { copyToClipboard } from '../utils/clipboard';

const PERSIST_KEY = 'creatorflow_ideas';
const PERSIST_MINUTES = 5;

const IdeaGenerator = () => {
  const { api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ideas, setIdeas] = useState(() => {
    const saved = localStorage.getItem(PERSIST_KEY);
    if (saved) {
      const { data, timestamp } = JSON.parse(saved);
      if (Date.now() - timestamp < PERSIST_MINUTES * 60 * 1000) return data;
      localStorage.removeItem(PERSIST_KEY);
    }
    return [];
  });
  const [form, setForm] = useState({
    platform: 'tiktok',
    niche: '',
    targetAudience: '',
    goal: 'growth'
  });

  useEffect(() => {
    if (ideas.length > 0) {
      localStorage.setItem(PERSIST_KEY, JSON.stringify({ data: ideas, timestamp: Date.now() }));
    }
  }, [ideas]);

  const hookColors = {
    educational: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30' },
    viral: { bg: 'bg-pink-500/10', text: 'text-pink-400', border: 'border-pink-500/30' },
    story: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' },
    contrarian: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30' }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!form.niche || !form.targetAudience) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/ideas/generate', form);
      setIdeas(res.data.ideas);
      toast.success(`Generated ${res.data.ideas.length} ideas! (${res.data.creditsUsed} credits used)`);
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate ideas');
    } finally {
      setLoading(false);
    }
  };

  const copyIdea = (idea) => {
    copyToClipboard(`"${idea.title}" – ${idea.description}`);
    toast.success('Copied to clipboard!');
  };

  const saveIdea = async (idea) => {
    try {
      await api.post('/saved', {
        type: 'idea',
        title: idea.title,
        content: idea
      });
      toast.success('Idea saved to library!');
    } catch (err) {
      toast.error('Failed to save idea');
    }
  };

  const createScript = (idea) => {
    navigate('/scripts', { state: { topic: idea.title } });
  };

  const generateHooks = (idea) => {
    navigate('/writing', { state: { topic: idea.title } });
  };

  const getPlatformLabel = (platform) => {
    if (platform === 'tiktok') return 'TikTok';
    if (platform === 'reels') return 'Reels';
    return 'Shorts';
  };

  return (
    <div className="space-y-6" data-testid="idea-generator">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold tracking-tight mb-2 bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
          Idea Generator
        </h1>
        <p className="text-slate-400 text-lg">
          Never run out of content ideas. Generate endless concepts tailored to your niche.
        </p>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Form */}
        <Card className="bg-slate-800/30 border-slate-700/50 h-fit">
          <CardContent className="p-6">
            <form onSubmit={handleGenerate} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-slate-300">Platform</Label>
                <Select 
                  value={form.platform} 
                  onValueChange={(v) => setForm({...form, platform: v})}
                >
                  <SelectTrigger data-testid="platform-select" className="bg-slate-900/50 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="tiktok">TikTok</SelectItem>
                    <SelectItem value="reels">Instagram Reels</SelectItem>
                    <SelectItem value="shorts">YouTube Shorts</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Niche / Topic</Label>
                <Input
                  data-testid="niche-input"
                  placeholder="e.g. Personal Finance, Fitness"
                  value={form.niche}
                  onChange={(e) => setForm({...form, niche: e.target.value})}
                  className="bg-slate-900/50 border-violet-500/50 text-white placeholder:text-slate-500 focus:border-violet-400"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Target Audience</Label>
                <Input
                  data-testid="audience-input"
                  placeholder="e.g. College students, Moms"
                  value={form.targetAudience}
                  onChange={(e) => setForm({...form, targetAudience: e.target.value})}
                  className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Goal</Label>
                <Select 
                  value={form.goal} 
                  onValueChange={(v) => setForm({...form, goal: v})}
                >
                  <SelectTrigger data-testid="goal-select" className="bg-slate-900/50 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="growth">Growth & Viral Reach</SelectItem>
                    <SelectItem value="viral">Go Viral</SelectItem>
                    <SelectItem value="community">Build Community</SelectItem>
                    <SelectItem value="authority">Establish Authority</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                type="submit" 
                className="w-full h-14 bg-gradient-to-r from-violet-500 to-cyan-500 hover:opacity-90 text-white text-lg font-medium"
                data-testid="generate-ideas-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating Ideas...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate Ideas
                  </>
                )}
              </Button>
              <p className="text-center text-slate-500 text-sm">Uses 2 credits</p>
            </form>
          </CardContent>
        </Card>

        {/* Right Column - Results */}
        <div className="space-y-4">
          {ideas.length === 0 ? (
            /* Empty State */
            <div className="border-2 border-dashed border-slate-700/50 rounded-xl p-12 text-center">
              <div className="flex justify-center mb-4">
                <Lightbulb className="w-16 h-16 text-slate-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-400 mb-2">No ideas generated yet</h3>
              <p className="text-slate-500">
                Fill out the form on the left to start generating content ideas.
              </p>
            </div>
          ) : (
            /* Ideas List */
            ideas.map((idea) => {
              const hookStyle = hookColors[idea.hookAngle] || hookColors.educational;
              return (
                <Card 
                  key={idea.id} 
                  className="bg-slate-800/30 border-slate-700/50"
                  data-testid="idea-card"
                >
                  <CardContent className="p-5">
                    {/* Tags */}
                    <div className="flex items-center gap-2 mb-4">
                      <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/30">
                        {getPlatformLabel(form.platform)}
                      </Badge>
                      <Badge className={`${hookStyle.bg} ${hookStyle.text} ${hookStyle.border}`}>
                        {idea.hookAngle === 'story' ? 'Story-based' : idea.hookAngle.charAt(0).toUpperCase() + idea.hookAngle.slice(1)}
                      </Badge>
                    </div>

                    {/* Idea Content */}
                    <p className="text-white text-lg leading-relaxed mb-4">
                      "{idea.title}" – {idea.description}
                    </p>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-4 mb-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyIdea(idea)}
                        className="text-slate-400 hover:text-white"
                        data-testid="copy-idea-btn"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => saveIdea(idea)}
                        className="text-cyan-400 hover:text-cyan-300"
                        data-testid="save-idea-btn"
                      >
                        <Bookmark className="w-4 h-4 mr-2" />
                        Save
                      </Button>
                    </div>

                    {/* Next Step */}
                    <div className="flex items-center gap-3 pt-4 border-t border-slate-700/50">
                      <span className="text-slate-500 text-sm">→ Next step: Turn this into a script or hooks</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => createScript(idea)}
                        className="text-violet-400 hover:text-violet-300"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Create Script
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => generateHooks(idea)}
                        className="text-violet-400 hover:text-violet-300"
                      >
                        <PenTool className="w-4 h-4 mr-1" />
                        Generate Hooks
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default IdeaGenerator;
